import React from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  CircularProgress,
  TableSortLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";

export default function DataTable({
  columns = [],
  rows = [],
  loading = false,
  pagination,
  orderBy,
  orderDirection = "asc",
  onRequestSort,
  getRowId = (row) => row.id,
  getRowTestId,
  emptyMessage = "Nenhum registro encontrado",
  containerSx,
  tableProps = {},

  // novos (opcionais)
  responsive = true,           // se quiser desativar o modo "cards" no mobile: responsive={false}
  mobileBreakpoint = "sm",     // até qual breakpoint vira card (sm, md, etc.)
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const hasPagination = Boolean(pagination);

  const page = hasPagination ? pagination.page : 0;
  const rowsPerPage = hasPagination
    ? pagination.rowsPerPage
    : safeRows.length || 10;
  const total = hasPagination ? pagination.total : safeRows.length;
  const rowsPerPageOptions =
    (pagination && pagination.rowsPerPageOptions) || [5, 10, 15, 25, 50];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));

  const visibleColumnsMobile = columns.filter(
    (col) => !col.hideOnMobile // nova flag opcional na coluna
  );

  // ====== RENDER MOBILE (Cards) ======
  const renderMobile = () => {
    // loading
    if (loading) {
      return (
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      );
    }

    // sem dados
    if (safeRows.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2, ...containerSx }}>
        {safeRows.map((row) => {
          const rowId = getRowId(row);
          const rowTestId = getRowTestId ? getRowTestId(row) : undefined;

          return (
            <Box
              key={rowId}
              data-testid={rowTestId}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              {visibleColumnsMobile.map((col) => {
                const value = col.render ? col.render(row) : row[col.id];

                return (
                  <Box
                    key={col.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.5,
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600, minWidth: 80 }}
                    >
                      {col.label}
                    </Typography>

                    <Box
                      sx={{
                        textAlign:
                          col.align === "right"
                            ? "right"
                            : col.align === "center"
                              ? "center"
                              : "left",
                        flex: 1,
                      }}
                    >
                      {value}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    );
  };

  // ====== RENDER DESKTOP (Tabela normal) ======
  const renderTable = () => (
    <Box
      sx={{
        p: 2,
        pb: hasPagination ? 0 : 2,
        overflowX: "auto",
        ...containerSx,
      }}
    >
      <TableContainer>
        <Table className="data-table" size="small" {...tableProps}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const isSorted = orderBy === col.id;
                const sortable =
                  col.sortable && typeof onRequestSort === "function";

                return (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={col.headSx}
                    sortDirection={isSorted ? orderDirection : false}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={isSorted}
                        direction={isSorted ? orderDirection : "asc"}
                        onClick={() => onRequestSort(col.id)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="left">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : safeRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="left">
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              safeRows.map((row) => {
                const rowId = getRowId(row);
                const rowTestId = getRowTestId
                  ? getRowTestId(row)
                  : undefined;

                return (
                  <TableRow key={rowId} data-testid={rowTestId} hover>
                    {columns.map((col) => (
                      <TableCell key={col.id} align={col.align || "left"}>
                        {col.render ? col.render(row) : row[col.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      {/* Mobile: cards / Desktop: tabela */}
      {responsive && isMobile ? renderMobile() : renderTable()}

      {/* Paginação (comum aos dois modos) */}
      {hasPagination && (
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={pagination.onPageChange}
          onRowsPerPageChange={pagination.onRowsPerPageChange}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      )}
    </>
  );
}
